import { WEBINAR_STORAGE_KEYS } from '../constants/webinar.constants'
import type {
  WebinarCredential,
  WebinarRecord,
} from '../models/webinar.model'

const readArray = <T>(key: string): T[] => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed as T[]
    // Legacy: previously the store used zustand/middleware/persist which
    // wraps the value as `{ state: { webinars: [...] }, version: N }`.
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.state &&
      Array.isArray(parsed.state.webinars)
    ) {
      return parsed.state.webinars as T[]
    }
    return []
  } catch {
    return []
  }
}

const writeArray = <T>(key: string, value: T[]) => {
  localStorage.setItem(key, JSON.stringify(value))
}

const readMap = <V>(key: string): Record<string, V> => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, V>) : {}
  } catch {
    return {}
  }
}

const writeMap = <V>(key: string, value: Record<string, V>) => {
  localStorage.setItem(key, JSON.stringify(value))
}

const DEFAULT_REGISTRATION = (
  data?: Partial<WebinarRecord['registration']>,
): WebinarRecord['registration'] => ({
  enabled: data?.enabled ?? false,
  fields: data?.fields ?? [
    { field: 'Full Name', required: true },
    { field: 'Email', required: true },
  ],
  domainRestrictionEnabled: data?.domainRestrictionEnabled ?? false,
  domainRestrictionType: data?.domainRestrictionType ?? 1,
  authorizedDomains: data?.authorizedDomains ?? [],
  restrictedDomains: data?.restrictedDomains ?? [],
  automaticMailApproval: data?.automaticMailApproval ?? true,
  registrationLimit: data?.registrationLimit ?? 100,
  bannerUrl: data?.bannerUrl ?? '',
})

const DEFAULT_NOTIFICATIONS = (
  data?: Partial<WebinarRecord['notifications']>,
): WebinarRecord['notifications'] => ({
  reminders: data?.reminders ?? {
    firstReminder: { enabled: false, time: 0 },
    secondReminder: { enabled: false, time: 0 },
    thirdReminder: { enabled: false, time: 0 },
  },
  postWebinar: data?.postWebinar ?? {
    thankYouEmail: { enabled: false, time: 2 },
    followUpEmail: { enabled: false, time: 2 },
  },
})

const DEFAULT_BRANDING = (
  data?: Partial<WebinarRecord['branding']>,
): WebinarRecord['branding'] => ({
  theme: data?.theme ?? { primary: '#042531', navBar: '#043040' },
  logoUrl: data?.logoUrl ?? '',
  backgroundUrl: data?.backgroundUrl ?? '',
  introVideoUrl: data?.introVideoUrl ?? '',
})

const DEFAULT_ENGAGEMENT = (
  data?: Partial<WebinarRecord['engagement']>,
): WebinarRecord['engagement'] => ({
  chat: data?.chat ?? true,
  chatVisibility: data?.chatVisibility ?? 'host',
  liveQandA: data?.liveQandA ?? false,
  reactions: data?.reactions ?? true,
  pollsEnabled: data?.pollsEnabled ?? false,
  resources: data?.resources ?? false,
  surveysEnabled: data?.surveysEnabled ?? false,
})

const DEFAULT_SECURITY = (
  data?: Partial<WebinarRecord['security']>,
): WebinarRecord['security'] => ({
  passwordEnabled: data?.passwordEnabled ?? false,
  allowJoinAnytime: data?.allowJoinAnytime ?? false,
  recording: data?.recording ?? true,
})

/** Normalise legacy records so consumers can safely read every nested field. */
const normalizeRecord = (raw: Partial<WebinarRecord>): WebinarRecord => {
  const id = (raw.id as string | undefined) ?? ''
  const durationHrs = (raw.durationHrs as number | undefined) ?? 0
  const durationMins = (raw.durationMins as number | undefined) ?? 0
  const duration =
    (raw.duration as number | undefined) ?? durationHrs * 60 + durationMins
  const now = Date.now()
  return {
    id,
    title: (raw.title as string | undefined) ?? 'Untitled webinar',
    description: (raw.description as string | undefined) ?? '',
    startTimeMs: (raw.startTimeMs as number | undefined) ?? now,
    endTimeMs:
      (raw.endTimeMs as number | undefined) ??
      ((raw.startTimeMs as number | undefined) ?? now) + duration * 60_000,
    duration,
    durationHrs,
    durationMins,
    date: (raw.date as string | undefined) ?? '',
    time: (raw.time as string | undefined) ?? '',
    timezone: (raw.timezone as string | undefined) ?? 'UTC',
    additionalTimezones: Array.isArray(raw.additionalTimezones)
      ? raw.additionalTimezones
      : [],
    hostName: (raw.hostName as string | undefined) ?? '',
    hostEmail: (raw.hostEmail as string | undefined) ?? '',
    hostId: raw.hostId as string | undefined,
    invitees: Array.isArray(raw.invitees) ? raw.invitees : [],
    maxRegistrants: (raw.maxRegistrants as number | undefined) ?? 100,
    registrants: Array.isArray(raw.registrants) ? raw.registrants : [],
    passwordEnabled: !!raw.passwordEnabled,
    password: (raw.password as string | undefined) ?? '',
    anyTimeJoin: !!raw.anyTimeJoin,
    recording: raw.recording === undefined ? true : !!raw.recording,
    status: (raw.status as WebinarRecord['status'] | undefined) ?? 'upcoming',
    thumbnail: raw.thumbnail as string | undefined,
    recurring: !!raw.recurring,
    registration: DEFAULT_REGISTRATION(raw.registration),
    notifications: DEFAULT_NOTIFICATIONS(raw.notifications),
    branding: DEFAULT_BRANDING(raw.branding),
    engagement: DEFAULT_ENGAGEMENT(raw.engagement),
    security: DEFAULT_SECURITY({
      ...raw.security,
      passwordEnabled: !!raw.passwordEnabled,
      allowJoinAnytime: !!raw.anyTimeJoin,
      recording: raw.recording === undefined ? true : !!raw.recording,
    }),
    speakers: Array.isArray(raw.speakers) ? raw.speakers : [],
    polls: Array.isArray(raw.polls) ? raw.polls : [],
    survey: raw.survey ?? null,
    createdAt: (raw.createdAt as number | undefined) ?? now,
    updatedAt: (raw.updatedAt as number | undefined) ?? now,
  }
}

// ── credentials ─────────────────────────────────────────────────────────

export const webinarCredentialsRepo = {
  all(): Record<string, WebinarCredential> {
    return readMap<WebinarCredential>(WEBINAR_STORAGE_KEYS.CREDENTIALS)
  },
  get(webinarId: string): WebinarCredential | undefined {
    return this.all()[webinarId]
  },
  put(credential: WebinarCredential): void {
    const map = this.all()
    map[credential.webinarId] = credential
    writeMap(WEBINAR_STORAGE_KEYS.CREDENTIALS, map)
  },
  remove(webinarId: string): void {
    const map = this.all()
    if (!(webinarId in map)) return
    delete map[webinarId]
    writeMap(WEBINAR_STORAGE_KEYS.CREDENTIALS, map)
  },
}

// ── records ─────────────────────────────────────────────────────────────

export const webinarRepo = {
  all(): WebinarRecord[] {
    return readArray<Partial<WebinarRecord>>(WEBINAR_STORAGE_KEYS.RECORDS).map(
      normalizeRecord,
    )
  },

  findById(id: string): WebinarRecord | undefined {
    return this.all().find((w) => w.id === id)
  },

  insert(record: WebinarRecord): WebinarRecord {
    const all = this.all()
    if (all.some((w) => w.id === record.id)) {
      throw new Error(`Webinar with id ${record.id} already exists`)
    }
    writeArray(WEBINAR_STORAGE_KEYS.RECORDS, [record, ...all])
    return record
  },

  update(id: string, patch: Partial<WebinarRecord>): WebinarRecord | null {
    const all = this.all()
    const idx = all.findIndex((w) => w.id === id)
    if (idx === -1) return null
    const updated: WebinarRecord = {
      ...all[idx],
      ...patch,
      updatedAt: Date.now(),
    }
    all[idx] = updated
    writeArray(WEBINAR_STORAGE_KEYS.RECORDS, all)
    return updated
  },

  remove(id: string): boolean {
    const all = this.all()
    const next = all.filter((w) => w.id !== id)
    if (next.length === all.length) return false
    writeArray(WEBINAR_STORAGE_KEYS.RECORDS, next)
    webinarCredentialsRepo.remove(id)
    return true
  },

  bulkReplace(records: WebinarRecord[]): void {
    writeArray(WEBINAR_STORAGE_KEYS.RECORDS, records)
  },
}
